<script setup lang="ts">
import {useReportingStore} from "@/store/reporting";
import {ref, computed} from "vue";
import ReportPeriodFilterBar from "@/components/Reporting/ReportPeriodFilterBar.vue";
import PageHeader from "@/components/PageHeader.vue";
import UsageSummaryMetricTiles from "@/pages/Insights/ReportsDetailPage/components/UsageSummaryMetricTiles.vue";
import SubjectChoicePieChart from "@/pages/Insights/ReportsDetailPage/components/SubjectChoicePieChart.vue";
import SubjectChoiceBarGraphs from "@/pages/Insights/ReportsDetailPage/components/SubjectChoiceBarGraphs.vue";
import SubjectChoiceList from "@/pages/Insights/ReportsDetailPage/components/SubjectChoiceList.vue";
import AuthenticatedLayout from "@/layout/AuthenticatedLayout.vue";

const reportingStore = useReportingStore();
const metric = ref();
const reportData = ref();
const showReport = ref(false);
const pieChartChoice = ref("first_language");
const electiveDistributionChoice = ref("all");

const breadcrumbItems = computed(() => [
  { text: "Insights", to: { name: "reports" } },
  { text: "Subject Choice Distribution Report", to: null }
]);

reportingStore.setAvailableFilters({
  tenants: true,
  institutions: true,
  cohorts: true,
  curriculum: true
});

async function runReport() {
  await fetchMetricData();
  await fetchReportData();
  showReport.value = true;
}

function resetReport() {
  metric.value = null;
  reportData.value = null;
  showReport.value = false;
}

const curriculum = computed(() => reportingStore.filters.curriculum || "national_senior_certificate");

async function fetchReportData() {
  reportData.value = await reportingStore.fetchReport("subject-choice-distribution");
}

async function fetchMetricData() {
  metric.value = await reportingStore.fetchReport("subject-choice-usage-ratio");
}
</script>

<template>
  <authenticated-layout :back-route="{ name: 'reports' }" :breadcrumbs="breadcrumbItems">

    <page-header
      title="Subject Choice Distribution Report"
      description="Explore user the distribution of Subject Choices"
    />

    <ReportPeriodFilterBar
      :show-filters="true"
      :show-time-frame="false"
      :show-time-frame-switch="true"
      :show-run-report-button="true"
      @run-report="runReport"
      @reset-filters="resetReport"
    />

    <template v-if="showReport">
      <v-row>
        <v-col cols="12">
          <UsageSummaryMetricTiles
            :data="metric"
            category-title="Users"
            category-icon="fa fa-barcode"
            completed-label="Complete"
            pending-label="Incomplete"
            percentage-label="Completion Rate"
          />
        </v-col>
      </v-row>

      <template v-if="reportData">
        <template v-if="reportData.choices && curriculum === 'national_senior_certificate'">
          <v-row>
            <v-col cols="4">
              <v-card variant="flat" class="metric-tile">
                <v-card-text>
                  <v-row>
                    <v-col cols="auto">
                      <v-icon>far fa-book</v-icon>
                    </v-col>
                    <v-col cols="auto">
                      <h2>First Language</h2>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <SubjectChoiceList :list-data="reportData.choices.first_language"
                                         curriculum-type="national_senior_certificate"/>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card variant="flat" class="metric-tile">
                <v-card-text>
                  <v-row>
                    <v-col cols="auto">
                      <v-icon>far fa-book</v-icon>
                    </v-col>
                    <v-col cols="auto">
                      <h2>Second Language</h2>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <SubjectChoiceList :list-data="reportData.choices.second_language"
                                         curriculum-type="national_senior_certificate"/>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card variant="flat" class="metric-tile">
                <v-card-text>
                  <v-row>
                    <v-col cols="auto">
                      <v-icon>far fa-book</v-icon>
                    </v-col>
                    <v-col cols="auto">
                      <h2>Top 10 Electives</h2>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <SubjectChoiceList :list-data="reportData.electiveTotals"
                                         curriculum-type="national_senior_certificate"/>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <v-card variant="flat" class="metric-tile">
                <v-card-text>
                  <v-row>
                    <v-col cols="auto">
                      <v-icon>far fa-pie-chart</v-icon>
                    </v-col>
                    <v-col cols="auto">
                      <h2>Subject Choices</h2>
                    </v-col>
                    <v-spacer/>
                    <v-col>
                      <v-select
                        v-model="pieChartChoice"
                        variant="outlined"
                        :items="
                          ['first_language',
                           'second_language',
                           'mathematics',
                           'elective_1',
                           'elective_2',
                           'elective_3',
                           'elective_4',
                           'elective_5']"
                        label="Subject Choices"
                        class="elevation-0"
                      />

                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col>
                      <SubjectChoicePieChart :report-data="reportData.choices[pieChartChoice]"
                                             :subject-group="pieChartChoice"
                                             curriculum="national_senior_certificate"/>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="6">
              <v-card variant="flat" class="metric-tile" height="100%">
                <v-card-text>
                  <v-row>
                    <v-col cols="auto">
                      <v-icon>far fa-pie-chart</v-icon>
                    </v-col>
                    <v-col cols="auto">
                      <h2>Elective Distribution</h2>
                    </v-col>
                    <v-spacer/>
                    <v-col>
                      <v-select
                        v-model="electiveDistributionChoice"
                        variant="outlined"
                        :items="['all', 'elective_1', 'elective_2', 'elective_3', 'elective_4', 'elective_5']"
                        label="Elective Choices"
                      />

                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col>
                      <SubjectChoiceBarGraphs :report-data="reportData.choices" curriculum="national_senior_certificate"
                                              :elective-group="electiveDistributionChoice"/>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>
        <v-row v-else>
          <v-col cols="12" class="text-center my-12">
            <h1 class="grey--text">
              No data available.
            </h1>
          </v-col>
        </v-row>
      </template>
    </template>
  </authenticated-layout>
</template>

<style scoped>

</style>
